import { useState, useMemo } from 'react'
import { Modal, Radio, Select, Space, Checkbox, Tag, Alert, Typography, Table } from 'antd'
import type { RecvizPeriod, GenerateRecvizMultiResponse, PeriodResult } from '@/api/recviz'
import { expandPeriodRange, formatPeriod, periodToSortKey, isValidPeriod } from '@/utils/periodUtils'

const { Text } = Typography

interface GenerateMultiPeriodModalProps {
  visible: boolean
  onClose: () => void
  availablePeriods: RecvizPeriod[]
  onGenerate: (periods: string[], exportToCh: boolean) => Promise<void>
  loading: boolean
  result: GenerateRecvizMultiResponse | null
}

export function GenerateMultiPeriodModal({
  visible,
  onClose,
  availablePeriods,
  onGenerate,
  loading,
  result,
}: GenerateMultiPeriodModalProps) {
  const [mode, setMode] = useState<'range' | 'list'>('range')
  const [periodFrom, setPeriodFrom] = useState<string>('')
  const [periodTo, setPeriodTo] = useState<string>('')
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])
  const [exportToCh, setExportToCh] = useState(true)

  // Build period options (known from PG + allow custom input)
  const periodOptions = useMemo(() => {
    const known = new Set(availablePeriods.map(p => p.period))
    const all = availablePeriods.map(p => ({
      value: p.period,
      label: `${p.label || formatPeriod(p.period)}${known.has(p.period) ? '' : ' (шаблон)'}`,
    }))
    // Sort chronologically
    return all.sort((a, b) => periodToSortKey(a.value) - periodToSortKey(b.value))
  }, [availablePeriods])

  // Expanded periods for range mode
  const expandedPeriods = useMemo(() => {
    if (mode !== 'range' || !periodFrom || !periodTo) return []
    if (periodToSortKey(periodFrom) > periodToSortKey(periodTo)) return []
    return expandPeriodRange(periodFrom, periodTo)
  }, [mode, periodFrom, periodTo])

  // Final period list based on mode
  const finalPeriods = mode === 'range' ? expandedPeriods : selectedPeriods

  const pgPeriods = useMemo(() => new Set(availablePeriods.map(p => p.period)), [availablePeriods])

  const handleGenerate = async () => {
    if (finalPeriods.length === 0) return
    await onGenerate(finalPeriods, exportToCh)
  }

  // Result columns
  const resultColumns = [
    {
      title: 'Период',
      dataIndex: 'period',
      key: 'period',
      render: (period: string) => formatPeriod(period),
    },
    {
      title: 'Записей',
      dataIndex: 'rows_count',
      key: 'rows_count',
      width: 100,
      align: 'right' as const,
    },
    {
      title: 'Источник',
      dataIndex: 'fallback',
      key: 'fallback',
      width: 120,
      render: (fallback: boolean) => fallback
        ? <Tag color="orange">шаблон</Tag>
        : <Tag color="green">PG</Tag>,
    },
    {
      title: 'Экспорт',
      dataIndex: 'export_status',
      key: 'export_status',
      width: 200,
      render: (status: PeriodResult['export_status']) => {
        if (!status) return <Text type="secondary">-</Text>
        return status.success
          ? <Tag color="green">{status.rows_exported} записей</Tag>
          : <Tag color="red">{status.error}</Tag>
      },
    },
  ]

  return (
    <Modal
      title="Формирование справочника реквизитов"
      open={visible}
      onCancel={onClose}
      width={700}
      okText={result ? 'Закрыть' : 'Сформировать'}
      onOk={result ? onClose : handleGenerate}
      okButtonProps={{
        loading,
        disabled: !result && finalPeriods.length === 0,
      }}
      cancelButtonProps={{ style: result ? { display: 'none' } : undefined }}
    >
      {!result ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Mode selector */}
          <Radio.Group value={mode} onChange={e => setMode(e.target.value)}>
            <Radio.Button value="range">Диапазон</Radio.Button>
            <Radio.Button value="list">Выбрать периоды</Radio.Button>
          </Radio.Group>

          {/* Range mode */}
          {mode === 'range' && (
            <Space size="middle">
              <div>
                <Text type="secondary">От:</Text>
                <Select
                  style={{ width: 180, marginLeft: 8 }}
                  placeholder="ММГГ"
                  value={periodFrom || undefined}
                  onChange={setPeriodFrom}
                  options={periodOptions}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
                    (option?.value ?? '').includes(input)
                  }
                />
              </div>
              <div>
                <Text type="secondary">До:</Text>
                <Select
                  style={{ width: 180, marginLeft: 8 }}
                  placeholder="ММГГ"
                  value={periodTo || undefined}
                  onChange={setPeriodTo}
                  options={periodOptions}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
                    (option?.value ?? '').includes(input)
                  }
                />
              </div>
            </Space>
          )}

          {/* List mode */}
          {mode === 'list' && (
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Выберите или введите периоды (ММГГ)"
              value={selectedPeriods}
              onChange={(values) => setSelectedPeriods(values.filter(isValidPeriod))}
              options={periodOptions}
              tokenSeparators={[',', ' ']}
            />
          )}

          {/* Expanded periods preview */}
          {finalPeriods.length > 0 && (
            <div>
              <Text type="secondary">Периоды ({finalPeriods.length}):</Text>
              <div style={{ marginTop: 8 }}>
                {finalPeriods.map(p => (
                  <Tag key={p} color={pgPeriods.has(p) ? 'green' : 'orange'}>
                    {formatPeriod(p)}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* Fallback info */}
          {finalPeriods.some(p => !pgPeriods.has(p)) && (
            <Alert
              type="info"
              showIcon
              message="Периоды без данных в БД будут сформированы по шаблону последнего доступного периода"
            />
          )}

          {/* Export checkbox */}
          <Checkbox checked={exportToCh} onChange={e => setExportToCh(e.target.checked)}>
            Экспортировать в ClickHouse
          </Checkbox>
        </Space>
      ) : (
        /* Results view */
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <Text type="secondary">Всего записей:</Text>{' '}
              <Text strong>{result.total_rows}</Text>
            </div>
            <div>
              <Text type="secondary">Периодов:</Text>{' '}
              <Text strong>{result.results.length}</Text>
            </div>
          </div>

          <Table
            columns={resultColumns}
            dataSource={result.results}
            rowKey="period"
            pagination={false}
            size="small"
          />
        </Space>
      )}
    </Modal>
  )
}
